import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Construction, Ruler, Droplets, Cable, Route, Plus, Trash2, Calculator,
  ChevronDown, ChevronUp, Settings, X, Check, Edit2, Info
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from './ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import { toast } from 'sonner';
import {
  concretePavingDatabase,
  drainageDatabase,
  utilityDatabase,
  roadSpecifications,
  gradingDatabase
} from '../data/civilMaterials';

const formatCurrency = (num) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(num);
};

const formatNumber = (num, decimals = 2) => {
  if (num === null || num === undefined || isNaN(num)) return '0';
  return Number(num).toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
};

const CivilCalculator = ({ civilItems, setCivilItems }) => {
  const [activeTab, setActiveTab] = useState('paving');
  const [isOpen, setIsOpen] = useState(true);
  const [showCustomManager, setShowCustomManager] = useState(false);

  // Custom Materials State
  const [customPavingSpecs, setCustomPavingSpecs] = useState(() => {
    const saved = localStorage.getItem('customPavingSpecs');
    return saved ? JSON.parse(saved) : [];
  });
  const [customDrainageItems, setCustomDrainageItems] = useState(() => {
    const saved = localStorage.getItem('customDrainageItems');
    return saved ? JSON.parse(saved) : [];
  });
  const [customUtilityItems, setCustomUtilityItems] = useState(() => {
    const saved = localStorage.getItem('customUtilityItems');
    return saved ? JSON.parse(saved) : [];
  });
  const [customGradingItems, setCustomGradingItems] = useState(() => {
    const saved = localStorage.getItem('customGradingItems');
    return saved ? JSON.parse(saved) : [];
  });

  // Custom item form state
  const [newCustomItem, setNewCustomItem] = useState({
    name: '', category: 'Custom', costPerUnit: '', unit: 'SY', description: ''
  });

  // Save custom materials to localStorage
  useEffect(() => {
    localStorage.setItem('customPavingSpecs', JSON.stringify(customPavingSpecs));
  }, [customPavingSpecs]);
  useEffect(() => {
    localStorage.setItem('customDrainageItems', JSON.stringify(customDrainageItems));
  }, [customDrainageItems]);
  useEffect(() => {
    localStorage.setItem('customUtilityItems', JSON.stringify(customUtilityItems));
  }, [customUtilityItems]);
  useEffect(() => {
    localStorage.setItem('customGradingItems', JSON.stringify(customGradingItems));
  }, [customGradingItems]);

  // Combined materials lists
  const allPavingSpecs = useMemo(() => [...roadSpecifications, ...customPavingSpecs], [customPavingSpecs]);
  const allDrainageItems = useMemo(() => [...drainageDatabase, ...customDrainageItems], [customDrainageItems]);
  const allUtilityItems = useMemo(() => [...utilityDatabase, ...customUtilityItems], [customUtilityItems]);
  const allGradingItems = useMemo(() => [...gradingDatabase, ...customGradingItems], [customGradingItems]);

  // Paving Calculator State
  const [pavingType, setPavingType] = useState('');
  const [pavingVolume, setPavingVolume] = useState('');
  const [pavingCost, setPavingCost] = useState('');

  // Drainage Calculator State
  const [drainageItem, setDrainageItem] = useState('');
  const [drainageQuantity, setDrainageQuantity] = useState('');
  const [drainageLength, setDrainageLength] = useState('');
  const [drainageCost, setDrainageCost] = useState('');

  // Utility Calculator State
  const [utilityItem, setUtilityItem] = useState('');
  const [utilityLength, setUtilityLength] = useState('');
  const [utilityDepth, setUtilityDepth] = useState('4'); // feet

  // Curb Calculator State
  const [curbType, setCurbType] = useState('');
  const [curbLength, setCurbLength] = useState('');

  // Grading Calculator State
  const [gradingType, setGradingType] = useState('');
  const [gradingQuantity, setGradingQuantity] = useState('');

  const generateId = useCallback(() => {
    return `civil_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  // Add Paving Item
  const addPavingItem = () => {
    if (!pavingType || !pavingVolume || !pavingCost) {
      toast.error('Please fill all paving fields (type, volume, cost)');
      return;
    }

    const spec = allPavingSpecs.find(s => s.name === pavingType);
    if (!spec) return;

    const cubicYards = parseFloat(pavingVolume);
    const costPerCY = parseFloat(pavingCost);
    const totalCost = cubicYards * costPerCY;

    const newItem = {
      id: generateId(),
      type: 'paving',
      name: spec.name,
      category: spec.category || 'Custom',
      cubicYards,
      costPerCY,
      unit: 'CY',
      totalCost,
      isCustom: spec.isCustom || false,
      addedAt: new Date().toISOString()
    };

    setCivilItems([...civilItems, newItem]);
    toast.success(`${spec.name} added`);
    setPavingType('');
    setPavingVolume('');
    setPavingCost('');
  };

  // Add custom paving spec
  const addCustomPavingSpec = () => {
    if (!newCustomItem.name || !newCustomItem.costPerUnit) {
      toast.error('Please enter name and cost');
      return;
    }
    const newSpec = {
      id: generateId(),
      name: newCustomItem.name,
      category: 'Custom',
      costPerCY: parseFloat(newCustomItem.costPerUnit),
      description: newCustomItem.description || 'Custom paving specification',
      isCustom: true
    };
    setCustomPavingSpecs([...customPavingSpecs, newSpec]);
    toast.success('Custom paving spec added');
    setNewCustomItem({ name: '', category: 'Custom', costPerUnit: '', unit: 'SY', description: '' });
  };

  // Add custom drainage item
  const addCustomDrainageSpec = () => {
    if (!newCustomItem.name || !newCustomItem.costPerUnit) {
      toast.error('Please enter name and cost');
      return;
    }
    const newSpec = {
      id: generateId(),
      name: newCustomItem.name,
      category: 'Custom',
      costPerLinearFt: newCustomItem.unit === 'LF' ? parseFloat(newCustomItem.costPerUnit) : 0,
      unitCost: newCustomItem.unit === 'EA' ? parseFloat(newCustomItem.costPerUnit) : 0,
      description: newCustomItem.description || 'Custom drainage item',
      isCustom: true
    };
    setCustomDrainageItems([...customDrainageItems, newSpec]);
    toast.success('Custom drainage item added');
    setNewCustomItem({ name: '', category: 'Custom', costPerUnit: '', unit: 'LF', description: '' });
  };

  // Add custom utility item
  const addCustomUtilitySpec = () => {
    if (!newCustomItem.name || !newCustomItem.costPerUnit) {
      toast.error('Please enter name and cost');
      return;
    }
    const newSpec = {
      id: generateId(),
      name: newCustomItem.name,
      category: 'Custom',
      costPerLinearFt: parseFloat(newCustomItem.costPerUnit),
      description: newCustomItem.description || 'Custom utility item',
      isCustom: true
    };
    setCustomUtilityItems([...customUtilityItems, newSpec]);
    toast.success('Custom utility item added');
    setNewCustomItem({ name: '', category: 'Custom', costPerUnit: '', unit: 'LF', description: '' });
  };

  // Add custom grading item
  const addCustomGradingSpec = () => {
    if (!newCustomItem.name || !newCustomItem.costPerUnit) {
      toast.error('Please enter name and cost');
      return;
    }
    const newSpec = {
      id: generateId(),
      name: newCustomItem.name,
      category: 'Custom',
      costPerCubicYard: newCustomItem.unit === 'CY' ? parseFloat(newCustomItem.costPerUnit) : 0,
      costPerSquareYard: newCustomItem.unit === 'SY' ? parseFloat(newCustomItem.costPerUnit) : 0,
      description: newCustomItem.description || 'Custom grading item',
      isCustom: true
    };
    setCustomGradingItems([...customGradingItems, newSpec]);
    toast.success('Custom grading item added');
    setNewCustomItem({ name: '', category: 'Custom', costPerUnit: '', unit: 'CY', description: '' });
  };

  // Delete custom item
  const deleteCustomItem = (type, id) => {
    if (type === 'paving') {
      setCustomPavingSpecs(customPavingSpecs.filter(i => i.id !== id));
    } else if (type === 'drainage') {
      setCustomDrainageItems(customDrainageItems.filter(i => i.id !== id));
    } else if (type === 'utility') {
      setCustomUtilityItems(customUtilityItems.filter(i => i.id !== id));
    } else if (type === 'grading') {
      setCustomGradingItems(customGradingItems.filter(i => i.id !== id));
    }
    toast.error('Custom item removed');
  };

  // Add Drainage Item
  const addDrainageItem = () => {
    if (!drainageItem) {
      toast.error('Please select a drainage item');
      return;
    }
    if (!drainageCost) {
      toast.error('Please enter cost per unit');
      return;
    }

    const item = allDrainageItems.find(d => d.name === drainageItem);
    if (!item) return;

    const qty = parseFloat(drainageQuantity) || 1;
    const len = parseFloat(drainageLength) || 0;
    const costPerUnit = parseFloat(drainageCost) || 0;

    let totalCost = 0;
    let totalWeight = 0;
    let unit = 'EA';

    if (len > 0) {
      // Pipe or linear item
      totalCost = costPerUnit * len;
      totalWeight = (item.weightPerLinearFt || 0) * len;
      unit = 'LF';
    } else {
      // Structure or each item
      totalCost = costPerUnit * qty;
      totalWeight = (item.weight || 0) * qty;
      unit = 'EA';
    }

    const newItem = {
      id: generateId(),
      type: 'drainage',
      name: item.name,
      category: item.category,
      quantity: qty,
      linearFeet: len,
      diameter: item.diameter || null,
      costPerUnit,
      unit,
      totalCost,
      totalWeight,
      totalTons: totalWeight / 2000,
      isCustom: item.isCustom || false,
      addedAt: new Date().toISOString()
    };

    setCivilItems([...civilItems, newItem]);
    toast.success(`${item.name} added`);
    setDrainageItem('');
    setDrainageQuantity('');
    setDrainageLength('');
    setDrainageCost('');
  };

  // Add Utility Item
  const addUtilityItem = () => {
    if (!utilityItem || !utilityLength) {
      toast.error('Please select utility and enter length');
      return;
    }

    const item = allUtilityItems.find(u => u.name === utilityItem);
    if (!item) return;

    const len = parseFloat(utilityLength);
    const depth = parseFloat(utilityDepth) || 4;
    const totalCost = (item.costPerLinearFt || 0) * len;
    const totalWeight = (item.weightPerLinearFt || 0) * len;

    // Estimate trench excavation
    const trenchWidth = (item.diameter || 12) / 12 + 2; // pipe diameter + 2ft
    const trenchCY = (len * trenchWidth * depth) / 27;

    const newItem = {
      id: generateId(),
      type: 'utility',
      name: item.name,
      category: item.category,
      linearFeet: len,
      depth,
      diameter: item.diameter || null,
      trenchCubicYards: trenchCY,
      totalCost,
      totalWeight,
      totalTons: totalWeight / 2000,
      isCustom: item.isCustom || false,
      addedAt: new Date().toISOString()
    };

    setCivilItems([...civilItems, newItem]);
    toast.success(`${item.name} added`);
    setUtilityItem('');
    setUtilityLength('');
    setUtilityDepth('4');
  };

  // Add Curb Item
  const addCurbItem = () => {
    if (!curbType || !curbLength) {
      toast.error('Please select curb type and enter length');
      return;
    }

    const curb = concretePavingDatabase.find(c => c.name === curbType);
    if (!curb) return;

    const len = parseFloat(curbLength);
    const totalCost = (curb.costPerLinearFt || 0) * len;
    const cubicYards = (curb.cubicYardsPerLinearFt || 0) * len;

    const newItem = {
      id: generateId(),
      type: 'curb',
      name: curb.name,
      category: 'Curb & Gutter',
      linearFeet: len,
      cubicYards,
      totalCost,
      addedAt: new Date().toISOString()
    };

    setCivilItems([...civilItems, newItem]);
    toast.success(`${curb.name} added`);
    setCurbType('');
    setCurbLength('');
  };

  // Add Grading Item
  const addGradingItem = () => {
    if (!gradingType || !gradingQuantity) {
      toast.error('Please select grading type and enter quantity');
      return;
    }

    const grading = allGradingItems.find(g => g.name === gradingType);
    if (!grading) return;

    const qty = parseFloat(gradingQuantity);
    const totalCost = (grading.costPerCubicYard || grading.costPerSquareYard || 0) * qty;

    const newItem = {
      id: generateId(),
      type: 'grading',
      name: grading.name,
      category: grading.category,
      quantity: qty,
      unit: grading.costPerCubicYard ? 'CY' : 'SY',
      totalCost,
      tonsPerCubicYard: grading.tonsPerCubicYard || null,
      totalTons: grading.tonsPerCubicYard ? qty * grading.tonsPerCubicYard : 0,
      isCustom: grading.isCustom || false,
      addedAt: new Date().toISOString()
    };

    setCivilItems([...civilItems, newItem]);
    toast.success(`${grading.name} added`);
    setGradingType('');
    setGradingQuantity('');
  };

  // Delete item
  const deleteItem = (id) => {
    setCivilItems(civilItems.filter(item => item.id !== id));
    toast.error('Item removed');
  };

  // Calculate totals
  const totals = civilItems.reduce((acc, item) => {
    acc.totalCost += item.totalCost || 0;
    acc.totalTons += item.totalTons || 0;
    acc.totalLinearFeet += item.linearFeet || 0;
    acc.totalSquareYards += item.squareYards || 0;
    acc.itemCount++;
    return acc;
  }, { totalCost: 0, totalTons: 0, totalLinearFeet: 0, totalSquareYards: 0, itemCount: 0 });

  // Filter items by category (use combined lists)
  const curbGutterItems = concretePavingDatabase.filter(c => c.category === 'Curb & Gutter');
  const drainPipes = allDrainageItems.filter(d => d.category === 'Storm Pipe' || d.category === 'Custom');
  const drainStructures = allDrainageItems.filter(d => d.category === 'Drainage Structures');
  const culverts = allDrainageItems.filter(d => d.category === 'Culverts');

  return (
    <div className="space-y-6">
      {/* Header Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Items</span>
              <Construction className="w-4 h-4 text-blue-500" />
            </div>
            <p className="text-2xl font-bold">{totals.itemCount}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-500/10 to-green-600/5">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Total Cost</span>
              <Calculator className="w-4 h-4 text-green-500" />
            </div>
            <p className="text-2xl font-bold">{formatCurrency(totals.totalCost)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-orange-500/10 to-orange-600/5">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Linear Feet</span>
              <Ruler className="w-4 h-4 text-orange-500" />
            </div>
            <p className="text-2xl font-bold">{formatNumber(totals.totalLinearFeet, 0)} LF</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-500/10 to-purple-600/5">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Square Yards</span>
              <Route className="w-4 h-4 text-purple-500" />
            </div>
            <p className="text-2xl font-bold">{formatNumber(totals.totalSquareYards, 0)} SY</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Calculator */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Construction className="w-5 h-5 text-primary" />
              Civil Construction Calculator
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="w-4 h-4 text-muted-foreground hover:text-primary cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent side="right" className="max-w-xs">
                    <p className="text-sm">Calculate costs for roads, paving, drainage, utilities, and site grading. Select material types and enter quantities to get instant cost estimates. You can also add custom materials.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <Dialog open={showCustomManager} onOpenChange={setShowCustomManager}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  <Settings className="w-4 h-4 mr-2" />
                  Manage Custom
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Manage Custom Civil Materials</DialogTitle>
                </DialogHeader>
                <Tabs defaultValue="paving" className="mt-4">
                  <TabsList className="grid grid-cols-4 mb-4">
                    <TabsTrigger value="paving">Paving</TabsTrigger>
                    <TabsTrigger value="drainage">Drainage</TabsTrigger>
                    <TabsTrigger value="utility">Utility</TabsTrigger>
                    <TabsTrigger value="grading">Grading</TabsTrigger>
                  </TabsList>

                  {/* Custom Paving */}
                  <TabsContent value="paving" className="space-y-4">
                    <div className="grid grid-cols-4 gap-2">
                      <Input placeholder="Name" value={newCustomItem.name} onChange={(e) => setNewCustomItem({...newCustomItem, name: e.target.value})} />
                      <Input type="number" placeholder="Cost/CY" value={newCustomItem.costPerUnit} onChange={(e) => setNewCustomItem({...newCustomItem, costPerUnit: e.target.value})} />
                      <Input placeholder="Description" value={newCustomItem.description} onChange={(e) => setNewCustomItem({...newCustomItem, description: e.target.value})} />
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button type="button" onClick={addCustomPavingSpec}><Plus className="w-4 h-4 mr-1" />Add</Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="text-sm">Add your own custom paving specification with your local pricing per cubic yard</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    {customPavingSpecs.length > 0 && (
                      <div className="space-y-2">
                        {customPavingSpecs.map(item => (
                          <div key={item.id} className="flex items-center justify-between p-2 bg-muted/30 rounded">
                            <span>{item.name} - {formatCurrency(item.costPerCY)}/CY</span>
                            <Button variant="ghost" size="sm" onClick={() => deleteCustomItem('paving', item.id)}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </TabsContent>

                  {/* Custom Drainage */}
                  <TabsContent value="drainage" className="space-y-4">
                    <div className="grid grid-cols-5 gap-2">
                      <Input placeholder="Name" value={newCustomItem.name} onChange={(e) => setNewCustomItem({...newCustomItem, name: e.target.value})} />
                      <Input type="number" placeholder="Cost" value={newCustomItem.costPerUnit} onChange={(e) => setNewCustomItem({...newCustomItem, costPerUnit: e.target.value})} />
                      <Select value={newCustomItem.unit} onValueChange={(v) => setNewCustomItem({...newCustomItem, unit: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="LF">Per LF</SelectItem>
                          <SelectItem value="EA">Each</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input placeholder="Description" value={newCustomItem.description} onChange={(e) => setNewCustomItem({...newCustomItem, description: e.target.value})} />
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button type="button" onClick={addCustomDrainageSpec}><Plus className="w-4 h-4 mr-1" />Add</Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="text-sm">Add custom drainage pipe or structure with your local pricing</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    {customDrainageItems.length > 0 && (
                      <div className="space-y-2">
                        {customDrainageItems.map(item => (
                          <div key={item.id} className="flex items-center justify-between p-2 bg-muted/30 rounded">
                            <span>{item.name} - {formatCurrency(item.costPerLinearFt || item.unitCost)}/{item.costPerLinearFt ? 'LF' : 'EA'}</span>
                            <Button variant="ghost" size="sm" onClick={() => deleteCustomItem('drainage', item.id)}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </TabsContent>

                  {/* Custom Utility */}
                  <TabsContent value="utility" className="space-y-4">
                    <div className="grid grid-cols-4 gap-2">
                      <Input placeholder="Name" value={newCustomItem.name} onChange={(e) => setNewCustomItem({...newCustomItem, name: e.target.value})} />
                      <Input type="number" placeholder="Cost/LF" value={newCustomItem.costPerUnit} onChange={(e) => setNewCustomItem({...newCustomItem, costPerUnit: e.target.value})} />
                      <Input placeholder="Description" value={newCustomItem.description} onChange={(e) => setNewCustomItem({...newCustomItem, description: e.target.value})} />
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button type="button" onClick={addCustomUtilitySpec}><Plus className="w-4 h-4 mr-1" />Add</Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="text-sm">Add custom utility pipe or conduit with your pricing per linear foot</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    {customUtilityItems.length > 0 && (
                      <div className="space-y-2">
                        {customUtilityItems.map(item => (
                          <div key={item.id} className="flex items-center justify-between p-2 bg-muted/30 rounded">
                            <span>{item.name} - {formatCurrency(item.costPerLinearFt)}/LF</span>
                            <Button variant="ghost" size="sm" onClick={() => deleteCustomItem('utility', item.id)}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </TabsContent>

                  {/* Custom Grading */}
                  <TabsContent value="grading" className="space-y-4">
                    <div className="grid grid-cols-5 gap-2">
                      <Input placeholder="Name" value={newCustomItem.name} onChange={(e) => setNewCustomItem({...newCustomItem, name: e.target.value})} />
                      <Input type="number" placeholder="Cost" value={newCustomItem.costPerUnit} onChange={(e) => setNewCustomItem({...newCustomItem, costPerUnit: e.target.value})} />
                      <Select value={newCustomItem.unit} onValueChange={(v) => setNewCustomItem({...newCustomItem, unit: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="CY">Per CY</SelectItem>
                          <SelectItem value="SY">Per SY</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input placeholder="Description" value={newCustomItem.description} onChange={(e) => setNewCustomItem({...newCustomItem, description: e.target.value})} />
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button type="button" onClick={addCustomGradingSpec}><Plus className="w-4 h-4 mr-1" />Add</Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="text-sm">Add custom grading/earthwork with your cost per cubic yard or square yard</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    {customGradingItems.length > 0 && (
                      <div className="space-y-2">
                        {customGradingItems.map(item => (
                          <div key={item.id} className="flex items-center justify-between p-2 bg-muted/30 rounded">
                            <span>{item.name} - {formatCurrency(item.costPerCubicYard || item.costPerSquareYard)}/{item.costPerCubicYard ? 'CY' : 'SY'}</span>
                            <Button variant="ghost" size="sm" onClick={() => deleteCustomItem('grading', item.id)}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </TabsContent>
                </Tabs>
              </DialogContent>
            </Dialog>
          </CardTitle>
          <CardDescription>
            Roads, paving, drainage, utilities, and grading calculations
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid grid-cols-5 mb-6">
              <TabsTrigger value="paving" className="text-xs">
                <Route className="w-3 h-3 mr-1" />Paving
              </TabsTrigger>
              <TabsTrigger value="drainage" className="text-xs">
                <Droplets className="w-3 h-3 mr-1" />Drainage
              </TabsTrigger>
              <TabsTrigger value="utilities" className="text-xs">
                <Cable className="w-3 h-3 mr-1" />Utilities
              </TabsTrigger>
              <TabsTrigger value="curb" className="text-xs">
                <Construction className="w-3 h-3 mr-1" />Curb
              </TabsTrigger>
              <TabsTrigger value="grading" className="text-xs">
                <Ruler className="w-3 h-3 mr-1" />Grading
              </TabsTrigger>
            </TabsList>

            {/* PAVING TAB */}
            <TabsContent value="paving" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Paving Type</Label>
                  <Select value={pavingType} onValueChange={setPavingType}>
                    <SelectTrigger><SelectValue placeholder="Select paving spec" /></SelectTrigger>
                    <SelectContent>
                      {allPavingSpecs.map(spec => (
                        <SelectItem key={spec.name} value={spec.name}>
                          {spec.isCustom && '⭐ '}{spec.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Volume (CY)</Label>
                  <Input
                    type="number"
                    placeholder="Cubic yards"
                    value={pavingVolume}
                    onChange={(e) => setPavingVolume(e.target.value)}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Cost ($/CY)</Label>
                <Input
                  type="number"
                  placeholder="Enter cost per cubic yard"
                  value={pavingCost}
                  onChange={(e) => setPavingCost(e.target.value)}
                />
              </div>

              {pavingType && pavingVolume && pavingCost && (
                <div className="p-4 bg-muted/50 rounded-lg">
                  <p className="text-sm font-medium mb-2">Preview:</p>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Volume:</span>
                      <span className="font-bold ml-2">
                        {formatNumber(parseFloat(pavingVolume), 1)} CY
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Est. Cost:</span>
                      <span className="font-bold ml-2 text-green-600">
                        {formatCurrency(parseFloat(pavingVolume) * parseFloat(pavingCost))}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              <Button onClick={addPavingItem} className="w-full">
                <Plus className="w-4 h-4 mr-2" />Add Paving
              </Button>
            </TabsContent>

            {/* DRAINAGE TAB */}
            <TabsContent value="drainage" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Drainage Item</Label>
                  <Select value={drainageItem} onValueChange={setDrainageItem}>
                    <SelectTrigger><SelectValue placeholder="Select item" /></SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      <div className="px-2 py-1 text-xs font-semibold bg-muted">Storm Pipe</div>
                      {drainPipes.map(item => (
                        <SelectItem key={item.name} value={item.name}>
                          {item.isCustom && '⭐ '}{item.name}
                        </SelectItem>
                      ))}
                      <div className="px-2 py-1 text-xs font-semibold bg-muted">Structures</div>
                      {drainStructures.map(item => (
                        <SelectItem key={item.name} value={item.name}>
                          {item.name}
                        </SelectItem>
                      ))}
                      <div className="px-2 py-1 text-xs font-semibold bg-muted">Culverts</div>
                      {culverts.map(item => (
                        <SelectItem key={item.name} value={item.name}>
                          {item.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Length (LF) - for pipes</Label>
                  <Input
                    type="number"
                    placeholder="Linear feet"
                    value={drainageLength}
                    onChange={(e) => setDrainageLength(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Quantity - for structures</Label>
                  <Input
                    type="number"
                    placeholder="1"
                    value={drainageQuantity}
                    onChange={(e) => setDrainageQuantity(e.target.value)}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Cost ($/LF or $/EA)</Label>
                <Input
                  type="number"
                  placeholder="Enter cost per unit"
                  value={drainageCost}
                  onChange={(e) => setDrainageCost(e.target.value)}
                />
              </div>
              <Button onClick={addDrainageItem} className="w-full">
                <Plus className="w-4 h-4 mr-2" />Add Drainage Item
              </Button>
            </TabsContent>

            {/* UTILITIES TAB */}
            <TabsContent value="utilities" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Utility Type</Label>
                  <Select value={utilityItem} onValueChange={setUtilityItem}>
                    <SelectTrigger><SelectValue placeholder="Select utility" /></SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {Object.entries(
                        allUtilityItems.reduce((acc, item) => {
                          if (!acc[item.category]) acc[item.category] = [];
                          acc[item.category].push(item);
                          return acc;
                        }, {})
                      ).map(([category, items]) => (
                        <React.Fragment key={category}>
                          <div className="px-2 py-1 text-xs font-semibold bg-muted">{category}</div>
                          {items.map(item => (
                            <SelectItem key={item.name} value={item.name}>
                              {item.isCustom && '⭐ '}{item.name}
                            </SelectItem>
                          ))}
                        </React.Fragment>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Length (LF)</Label>
                  <Input
                    type="number"
                    placeholder="Linear feet"
                    value={utilityLength}
                    onChange={(e) => setUtilityLength(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Trench Depth (ft)</Label>
                  <Input
                    type="number"
                    placeholder="4"
                    value={utilityDepth}
                    onChange={(e) => setUtilityDepth(e.target.value)}
                  />
                </div>
              </div>
              <Button onClick={addUtilityItem} className="w-full">
                <Plus className="w-4 h-4 mr-2" />Add Utility
              </Button>
            </TabsContent>

            {/* CURB TAB */}
            <TabsContent value="curb" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Curb & Gutter Type</Label>
                  <Select value={curbType} onValueChange={setCurbType}>
                    <SelectTrigger><SelectValue placeholder="Select curb type" /></SelectTrigger>
                    <SelectContent>
                      {curbGutterItems.map(item => (
                        <SelectItem key={item.name} value={item.name}>
                          <div className="flex justify-between w-full">
                            <span>{item.name}</span>
                            <span className="text-xs text-muted-foreground ml-2">
                              ${item.costPerLinearFt}/LF
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Length (LF)</Label>
                  <Input
                    type="number"
                    placeholder="Linear feet"
                    value={curbLength}
                    onChange={(e) => setCurbLength(e.target.value)}
                  />
                </div>
              </div>
              <Button onClick={addCurbItem} className="w-full">
                <Plus className="w-4 h-4 mr-2" />Add Curb & Gutter
              </Button>
            </TabsContent>

            {/* GRADING TAB */}
            <TabsContent value="grading" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Grading/Earthwork Type</Label>
                  <Select value={gradingType} onValueChange={setGradingType}>
                    <SelectTrigger><SelectValue placeholder="Select work type" /></SelectTrigger>
                    <SelectContent>
                      {allGradingItems.map(item => (
                        <SelectItem key={item.name} value={item.name}>
                          <div className="flex justify-between w-full">
                            <span>{item.isCustom && '⭐ '}{item.name}</span>
                            <span className="text-xs text-muted-foreground ml-2">
                              ${item.costPerCubicYard || item.costPerSquareYard}/{item.costPerCubicYard ? 'CY' : 'SY'}
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Quantity (CY or SY)</Label>
                  <Input
                    type="number"
                    placeholder="Enter quantity"
                    value={gradingQuantity}
                    onChange={(e) => setGradingQuantity(e.target.value)}
                  />
                </div>
              </div>
              <Button onClick={addGradingItem} className="w-full">
                <Plus className="w-4 h-4 mr-2" />Add Grading Work
              </Button>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Items List */}
      {civilItems.length > 0 && (
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
          <Card>
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Construction className="w-5 h-5" />
                    Civil Items ({civilItems.length})
                  </span>
                  {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </CardTitle>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent>
                <div className="space-y-3">
                  {civilItems.map(item => (
                    <div
                      key={item.id}
                      className="flex items-center justify-between p-4 bg-muted/30 rounded-lg"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{item.name}</span>
                          <Badge variant="outline" className="text-xs">{item.type}</Badge>
                        </div>
                        <div className="text-sm text-muted-foreground mt-1">
                          {item.linearFeet && <span>{formatNumber(item.linearFeet, 0)} LF • </span>}
                          {item.squareYards && <span>{formatNumber(item.squareYards, 0)} SY • </span>}
                          {item.quantity && item.unit && <span>{formatNumber(item.quantity, 0)} {item.unit} • </span>}
                          {item.totalTons > 0 && <span>{formatNumber(item.totalTons, 2)} tons • </span>}
                          <span className="text-green-600 font-medium">{formatCurrency(item.totalCost)}</span>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteItem(item.id)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>

                {/* Summary */}
                <div className="mt-6 p-4 bg-primary/10 rounded-lg">
                  <h4 className="font-semibold mb-3">Civil Work Summary</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Total Items:</span>
                      <span className="font-bold ml-2">{totals.itemCount}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Linear Feet:</span>
                      <span className="font-bold ml-2">{formatNumber(totals.totalLinearFeet, 0)}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Square Yards:</span>
                      <span className="font-bold ml-2">{formatNumber(totals.totalSquareYards, 0)}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Total Cost:</span>
                      <span className="font-bold ml-2 text-green-600">{formatCurrency(totals.totalCost)}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      )}
    </div>
  );
};

export default CivilCalculator;
